import React, { useState, useEffect, useRef } from "react";
import TrainingAPI from "../services/TrainingAPI";
import { formatDateOnly } from "../utils/DateTimeUtil";
import LoadingSpinner from "../common/LoadingSpinner";
import "./css/Training.css";
import "./css/TrainingProgress.css";

/**
 * Formats a category string to display-ready format.
 * Converts hyphenated names like "social-engineering" to "Social Engineering".
 */
const formatCategoryForDisplay = (category) => {
  if (!category) return "General";
  if (category.includes(" ")) return category;
  return category
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * TrainingProgress - Dashboard showing user's training progress, badges, and certificates
 *
 * Features:
 * - Overall progress statistics
 * - Active/In-progress trainings
 * - Recent completions
 * - Earned badges
 * - Certificates
 * - Recommendations
 */
const TrainingProgress = ({ onSelectTraining }) => {
  const [progress, setProgress] = useState(null);
  const [badges, setBadges] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const certificateRef = useRef(null);

  // Fixed dimensions for certificate download - landscape mode
  const CERTIFICATE_WIDTH = 900;
  const CERTIFICATE_HEIGHT = 600;

  // Function to download certificate as image using a fixed-size template
  const downloadCertificate = async () => {
    if (!selectedCertificate) return;

    setIsDownloading(true);
    try {
      // Create a temporary container with fixed dimensions
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "-9999px";
      document.body.appendChild(container);

      // Build the certificate HTML with fixed dimensions matching the UI
      container.innerHTML = `
        <div id="cert-download" style="
          width: ${CERTIFICATE_WIDTH}px;
          height: ${CERTIFICATE_HEIGHT}px;
          background: linear-gradient(135deg, #fffef8 0%, #fdf6e3 50%, #fffef8 100%);
          border: 8px solid #8b6914;
          box-sizing: border-box;
          position: relative;
          font-family: 'Georgia', 'Times New Roman', serif;
          padding: 30px 40px;
        ">
          <!-- Decorative corners -->
          <div style="position: absolute; top: 12px; left: 12px; width: 60px; height: 60px; border-top: 3px solid #8b6914; border-left: 3px solid #8b6914;"></div>
          <div style="position: absolute; top: 12px; right: 12px; width: 60px; height: 60px; border-top: 3px solid #8b6914; border-right: 3px solid #8b6914;"></div>
          <div style="position: absolute; bottom: 12px; left: 12px; width: 60px; height: 60px; border-bottom: 3px solid #8b6914; border-left: 3px solid #8b6914;"></div>
          <div style="position: absolute; bottom: 12px; right: 12px; width: 60px; height: 60px; border-bottom: 3px solid #8b6914; border-right: 3px solid #8b6914;"></div>
          
          <!-- Inner frame -->
          <div style="
            border: 2px solid #c9a227;
            height: 100%;
            box-sizing: border-box;
            padding: 15px 30px 20px 30px;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
          ">
            <!-- Header -->
            <div style="text-align: center;">
              <img src="/KinShieldIcon.png" alt="KinShield" style="width: 60px; height: 60px; margin-bottom: 6px; -webkit-user-drag: none; user-select: none;" crossorigin="anonymous" draggable="false" oncontextmenu="return false;" />
              <h1 style="font-size: 2rem; color: #1a365d; margin: 0 0 6px 0; font-weight: normal; letter-spacing: 3px; text-transform: uppercase;">Certificate of Completion</h1>
              <div style="margin: 6px 0; color: #c9a227; font-size: 1.1rem;">✦</div>
              <p style="font-size: 0.9rem; color: #666; margin: 0; font-style: italic;">KinShield Family Security Training Program</p>
            </div>
            
            <!-- Body -->
            <div style="text-align: center; flex: 1; display: flex; flex-direction: column; justify-content: center; padding: 10px 0;">
              <p style="font-size: 0.9rem; color: #666; margin: 4px 0;">This is to certify that</p>
              <h2 style="font-size: 2.2rem; color: #1a365d; margin: 4px 0; font-style: italic; font-weight: normal; border-bottom: 2px solid #c9a227; padding-bottom: 6px; display: inline-block;">${selectedCertificate.userName || "User"}</h2>
              <p style="font-size: 0.9rem; color: #666; margin: 4px 0;">has successfully completed the training course</p>
              <h3 style="font-size: 1.3rem; color: #8b6914; margin: 8px 0; font-weight: normal; white-space: nowrap;">"${selectedCertificate.trainingTitle}"</h3>
              ${
                selectedCertificate.quizScore
                  ? `
                <div style="margin-top: 10px; display: inline-flex; align-items: center; justify-content: center; gap: 8px;">
                  <span style="font-size: 0.9rem; color: #666;">Achieved a score of <strong style="color: #333;">${selectedCertificate.quizScore}%</strong></span>
                </div>
              `
                  : ""
              }
            </div>
            
            <!-- Footer -->
            <div style="width: 100%; text-align: center;">
              <div style="margin-bottom: 12px; text-align: center;">
                <div style="width: 200px; height: 1px; background: #333; margin: 0 auto 5px auto;"></div>
                <span style="font-size: 0.8rem; color: #666; font-style: italic;">KinShield Security Team</span>
              </div>
              <div style="display: flex; justify-content: center; gap: 60px;">
                <div style="text-align: center;">
                  <div style="font-size: 0.65rem; color: #8b6914; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; white-space: nowrap;">Date Issued</div>
                  <div style="font-size: 0.85rem; color: #333; white-space: nowrap;">${formatDate(selectedCertificate.issuedAt)}</div>
                </div>
                <div style="text-align: center;">
                  <div style="font-size: 0.65rem; color: #8b6914; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; white-space: nowrap;">Certificate ID</div>
                  <div style="font-size: 0.85rem; color: #333; white-space: nowrap;">${selectedCertificate.certificateNumber}</div>
                </div>
              </div>
            </div>
            
            <!-- Watermark -->
            <div style="
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-30deg);
              font-size: 5rem;
              font-weight: bold;
              color: rgba(201, 162, 39, 0.06);
              letter-spacing: 15px;
              pointer-events: none;
              white-space: nowrap;
            ">KINSHIELD</div>
          </div>
        </div>
      `;

      const certElement = container.querySelector("#cert-download");

      // Wait for the logo image to load
      const logoImg = certElement.querySelector("img");
      if (logoImg) {
        await new Promise((resolve) => {
          if (logoImg.complete) {
            resolve();
          } else {
            logoImg.onload = resolve;
            logoImg.onerror = resolve;
          }
        });
      }

      // Small delay to ensure rendering is complete
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Use dom-to-image-more with fixed dimensions
      const domtoimage = await import("dom-to-image-more");
      const dataUrl = await domtoimage.toPng(certElement, {
        width: CERTIFICATE_WIDTH,
        height: CERTIFICATE_HEIGHT,
        quality: 1,
        bgcolor: "#fffef8",
      });

      // Clean up
      document.body.removeChild(container);

      // Create download link
      const link = document.createElement("a");
      const fileName = `KinShield-Certificate-${selectedCertificate.certificateNumber}.png`;
      link.download = fileName;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading certificate:", err);
      alert("Failed to download certificate. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [progressRes, badgesRes, certsRes] = await Promise.all([
        TrainingAPI.getUserProgress(),
        TrainingAPI.getAllBadges(),
        TrainingAPI.getUserCertificates(),
      ]);

      if (progressRes.success) {
        setProgress(progressRes.progress);
      }
      if (badgesRes.success) {
        setBadges(badgesRes.badges || []);
      }
      if (certsRes.success) {
        setCertificates(certsRes.certificates || []);
      }
    } catch (err) {
      console.error("[TrainingProgress] Error fetching data:", err);
      setError("Failed to load progress. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Use centralized DateTimeUtil for consistent date formatting
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return formatDateOnly(dateStr, "medium");
  };

  if (loading) {
    return (
      <div className="training-container">
        <LoadingSpinner message="Loading progress..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="training-container">
        <div className="training-error">
          <div className="training-error-icon">!</div>
          <p className="training-error-message">{error}</p>
          <button className="btn btn-primary" onClick={fetchData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="training-container">
      {/* Progress Overview Card */}
      {progress && (
        <div className="training-progress-overview">
          <div className="progress-stats">
            <div className="progress-stat">
              <div className="progress-stat-value">
                {progress.completedTrainings || 0}
              </div>
              <div className="progress-stat-label">Completed</div>
            </div>
            <div className="progress-stat">
              <div className="progress-stat-value">
                {progress.inProgressTrainings || 0}
              </div>
              <div className="progress-stat-label">In Progress</div>
            </div>
            <div className="progress-stat">
              <div className="progress-stat-value">
                {progress.totalBadgesEarned || 0}
              </div>
              <div className="progress-stat-label">Badges Earned</div>
            </div>
            <div className="progress-stat">
              <div className="progress-stat-value">
                {progress.averageQuizScore?.toFixed(0) || 0}%
              </div>
              <div className="progress-stat-label">Avg. Score</div>
            </div>
            <div className="progress-stat">
              <div className="progress-stat-value">
                {progress.formattedTotalTime || "0 min"}
              </div>
              <div className="progress-stat-label">Time Spent</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="training-tabs">
        {[
          { id: "overview", label: "Overview" },
          {
            id: "badges",
            label: `Badges (${badges.filter((b) => b.userEarned).length}/${badges.length})`,
          },
          {
            id: "certificates",
            label: `Certificates (${certificates.length})`,
          },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`btn ${activeTab === tab.id ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && progress && (
        <div>
          {/* Active Sessions */}
          {progress.activeSessions && progress.activeSessions.length > 0 && (
            <div className="training-section">
              <h2 className="training-section-title">Continue Learning</h2>
              <div className="training-grid">
                {progress.activeSessions.map((session) => (
                  <div key={session.sessionId} className="training-card">
                    <span className="training-card-category">
                      {session.trainingCategory
                        ? session.trainingCategory.split(",")[0].trim()
                        : "General"}
                    </span>
                    <h3 className="training-card-title">
                      {session.trainingTitle}
                    </h3>
                    <div className="training-card-progress">
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${session.progressPercent || 0}%` }}
                        />
                      </div>
                      <div className="progress-bar-label">
                        <span>{session.progressPercent || 0}% complete</span>
                        <span>
                          {session.formattedTimeSpent || "0 min"} spent
                        </span>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary training-card-btn"
                      onClick={() =>
                        onSelectTraining &&
                        onSelectTraining({ uuid: session.trainingUuid })
                      }
                    >
                      Continue
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Completions */}
          {progress.recentCompletions &&
            progress.recentCompletions.length > 0 && (
              <div className="training-section">
                <h2 className="training-section-title">Recently Completed</h2>
                <div className="recent-completions-list">
                  {progress.recentCompletions.map((session) => (
                    <div
                      key={session.sessionId}
                      className="recent-completion-card"
                    >
                      <div className="completion-icon">Done</div>
                      <div className="completion-info">
                        <div className="completion-title">
                          {session.trainingTitle}
                        </div>
                        <div className="completion-meta">
                          Completed {formatDate(session.completedAt)} • Score:{" "}
                          {session.quizScore}%
                        </div>
                      </div>
                      <button
                        className="btn btn-ghost"
                        onClick={() =>
                          onSelectTraining &&
                          onSelectTraining({ uuid: session.trainingUuid })
                        }
                      >
                        Review
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Recommendations */}
          {progress.recommendedTrainings &&
            progress.recommendedTrainings.length > 0 && (
              <div className="training-section">
                <h2 className="training-section-title">Recommended for You</h2>
                <div className="training-grid">
                  {progress.recommendedTrainings.slice(0, 3).map((training) => (
                    <div key={training.trainingId} className="training-card">
                      <span className="training-card-category">
                        {formatCategoryForDisplay(
                          training.category
                            ? training.category.split(",")[0].trim()
                            : "General",
                        )}
                      </span>
                      <h3 className="training-card-title">{training.title}</h3>
                      <p className="training-card-description">
                        {training.summary ||
                          training.description?.substring(0, 100)}
                        ...
                      </p>
                      <div className="training-card-meta">
                        <span>{training.estimatedMinutes || 10} min</span>
                        <span>{training.difficultyLabel || "Beginner"}</span>
                      </div>
                      <button
                        className="btn btn-primary training-card-btn"
                        onClick={() =>
                          onSelectTraining && onSelectTraining(training)
                        }
                      >
                        Start Training
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Badges Tab */}
      {activeTab === "badges" && (
        <div>
          <h2 className="training-section-title">Your Badges</h2>
          {badges.length === 0 ? (
            <div className="training-empty">
              <div className="training-empty-icon">?</div>
              <p className="training-empty-message">No badges available yet</p>
            </div>
          ) : (
            <div className="badges-grid">
              {badges.map((badge) => (
                <div
                  key={badge.badgeId}
                  className={`badge-card ${badge.userEarned ? "earned" : "locked"}`}
                >
                  <div className="badge-icon">
                    {badge.userEarned ? "Earned" : "Locked"}
                  </div>
                  <div className="badge-name">{badge.name}</div>
                  <div className="badge-description">{badge.description}</div>
                  {badge.userEarned && badge.userEarnedAt && (
                    <div className="badge-earned-date">
                      Earned {formatDate(badge.userEarnedAt)}
                    </div>
                  )}
                  {!badge.userEarned && (
                    <div className="badge-earned-date">
                      {badge.totalEarned || 0} users earned this
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Certificates Tab */}
      {activeTab === "certificates" && (
        <div>
          <h2 className="training-section-title">Your Certificates</h2>
          {certificates.length === 0 ? (
            <div className="training-empty">
              <div className="training-empty-icon">?</div>
              <p className="training-empty-message">
                Complete trainings with a passing quiz score to earn
                certificates
              </p>
            </div>
          ) : (
            <div className="certificates-list">
              {certificates.map((cert) => (
                <div key={cert.certificateId} className="certificate-card">
                  <div className="certificate-icon">Cert</div>
                  <div className="certificate-info">
                    <div className="certificate-title">
                      {cert.trainingTitle}
                    </div>
                    <div className="certificate-meta">
                      Issued {formatDate(cert.issuedAt)} • Certificate:{" "}
                      <span className="certificate-number">
                        {cert.certificateNumber}
                      </span>
                      {cert.isExpired && (
                        <span className="certificate-expired-badge">
                          (Expired)
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedCertificate(cert)}
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Certificate Modal */}
      {selectedCertificate && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedCertificate(null)}
        >
          <div
            className="certificate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Actions Bar with Close and Download */}
            <div className="certificate-top-actions">
              <button
                className="certificate-download-btn"
                onClick={downloadCertificate}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <span className="download-spinner"></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <span className="download-icon">⬇</span>
                    Download
                  </>
                )}
              </button>
              <button
                className="modal-close-btn-top"
                onClick={() => setSelectedCertificate(null)}
              >
                ✕
              </button>
            </div>
            <div className="certificate-display" ref={certificateRef}>
              {/* Decorative corner elements */}
              <div className="certificate-corner certificate-corner-tl"></div>
              <div className="certificate-corner certificate-corner-tr"></div>
              <div className="certificate-corner certificate-corner-bl"></div>
              <div className="certificate-corner certificate-corner-br"></div>

              {/* Inner border frame */}
              <div className="certificate-inner-frame">
                <div className="certificate-header">
                  <div className="certificate-logo-container">
                    <img
                      src="/KinShieldIcon.png"
                      alt="KinShield"
                      className="certificate-logo-img"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </div>
                  <h1 className="certificate-title-main">
                    Certificate of Completion
                  </h1>
                  <div className="certificate-divider">
                    <span className="divider-ornament">✦</span>
                  </div>
                  <p className="certificate-subtitle">
                    KinShield Family Security Training Program
                  </p>
                </div>

                <div className="certificate-body">
                  <p className="certificate-awarded">This is to certify that</p>
                  <h2 className="certificate-name">
                    {selectedCertificate.userName || "User"}
                  </h2>
                  <p className="certificate-awarded">
                    has successfully completed the training course
                  </p>
                  <h3 className="certificate-training">
                    "{selectedCertificate.trainingTitle}"
                  </h3>
                  {selectedCertificate.quizScore && (
                    <div className="certificate-achievement">
                      <span className="achievement-text">
                        Achieved a score of{" "}
                        <strong>{selectedCertificate.quizScore}%</strong>
                      </span>
                    </div>
                  )}
                </div>

                <div className="certificate-footer">
                  <div className="certificate-signature">
                    <div className="signature-line"></div>
                    <span className="signature-label">
                      KinShield Security Team
                    </span>
                  </div>

                  <div className="certificate-details">
                    <div className="certificate-detail-item">
                      <span className="detail-label">Date Issued</span>
                      <span className="detail-value">
                        {formatDate(selectedCertificate.issuedAt)}
                      </span>
                    </div>
                    <div className="certificate-detail-item">
                      <span className="detail-label">Certificate ID</span>
                      <span className="detail-value">
                        {selectedCertificate.certificateNumber}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="certificate-watermark">KINSHIELD</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingProgress;
